import classnames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';
import * as ReactDOM from 'react-dom';
import handleClickOutside from 'react-onclickoutside';
import RelativePortal from '../RelativePortal';
import styles from './Tooltip.module.scss';
import { CSSTransition } from 'react-transition-group';

const TOOLTIP_TITLE = '';
const tooltipWidth = 340;
const POSITIONS = {
  LEFT: 'left',
  RIGHT: 'right',
  LEFT_TOP: 'left_top',
  RIGHT_TOP: 'right_top',
};
const maxFitSize = 350;

@handleClickOutside
class TooltipContainer extends React.PureComponent {
  static propTypes = {
    title: PropTypes.any,
    content: PropTypes.any.isRequired,
    closeByClick: PropTypes.bool,
    hasWrapper: PropTypes.bool,
    position: PropTypes.string, // left, right, left_top
    isDummy: PropTypes.bool,
    isFormClass: PropTypes.bool,
    fixed: PropTypes.bool,
    show: PropTypes.bool,
    handleWrapperClick: PropTypes.func,
    currentPosition: PropTypes.string,
    onClose: PropTypes.func,
    hasCloseIcon: PropTypes.bool,
  };

  handleClickOutside = () => {
    const { closeByClick, onClose } = this.props;
    closeByClick && onClose();
  };

  renderContainer = () => {
    const {
      hasWrapper,
      content,
      onClose,
      handleWrapperClick,
      currentPosition,
      title,
      hasCloseIcon,
      show,
    } = this.props;
    const classes = classnames(
      styles.tooltip,
      'help_block',
      'js-help-block',
      `help_block__${currentPosition}`,
      {
        help_block__active: show,
      },
    );
    const helpWrapClass = classnames('help_wrapper');
    return (
      <div className={classes}>
        <div onClick={handleWrapperClick} className={helpWrapClass}>
          {title !== null ? (
            <div className="help_title text-big">{title || TOOLTIP_TITLE}</div>
          ) : (
            title
          )}
          {hasCloseIcon && (
            <div className="icon icon_close js-help-close" onClick={onClose}>
              <span className="replace_text">Закрыть</span>
            </div>
          )}
          {hasWrapper ? <div>{content}</div> : content}
        </div>
      </div>
    );
  };

  render() {
    const { fixed, handleWrapperClick } = this.props;

    if (fixed) {
      return (
        // render tooltip in body if element's parent has overflow: hidden
        // use fixed = true for control this
        <RelativePortal onClick={handleWrapperClick}>{this.renderContainer()}</RelativePortal>
      );
    }

    return this.renderContainer();
  }
}

export default class Tooltip extends React.PureComponent {
  static propTypes = {
    title: PropTypes.any,
    content: PropTypes.any.isRequired,
    closeByClick: PropTypes.bool,
    hasWrapper: PropTypes.bool,
    position: PropTypes.oneOf(['left', 'right', 'left_top', 'inline', 'top']), // left, right, left_top
    isDummy: PropTypes.bool,
    isFormClass: PropTypes.bool,
    fixed: PropTypes.bool, // use for render tooltip container in relative portal,
    customIcon: PropTypes.string,
    hasCloseIcon: PropTypes.bool,
  };
  /*добавила hasWrapper, поскольку мне нужно в тултипах использовать не только инлайновые элементы*/
  static defaultProps = {
    closeByClick: true,
    hasWrapper: true,
    position: POSITIONS.RIGHT,
    isDummy: false,
    isFormClass: true,
    customIcon: 'icon_help',
    hasCloseIcon: true,
  };

  state = {
    position: null,
    show: false,
  };

  $containerRef = React.createRef();

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  offset = (el) => {
    const rect = el?.getBoundingClientRect(),
      scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: (rect?.top || 0) + scrollTop, left: (rect?.left || 0) + scrollLeft };
  };

  handleFit = () => {
    const reacts = this.$containerRef.current;
    const windowHeightToFooter = this.offset(document.querySelector('.footer')).top;
    const windowHeightToTooltip = this.offset(reacts).top;
    const windowHeightToTooltipBottom = windowHeightToTooltip + maxFitSize;
    const currentPosition = this.state.position || this.props.position;

    const windowWidth = window.innerWidth;
    const tooltipLeftOffset = this.offset(reacts).left;
    const availablePlace = windowWidth - tooltipLeftOffset;

    if (windowHeightToFooter < windowHeightToTooltipBottom) {
      if (currentPosition === POSITIONS.LEFT || availablePlace < tooltipWidth) {
        this.setState({ position: POSITIONS.LEFT_TOP });
      } else if (this.state.position === null) {
        this.setState({ position: POSITIONS.RIGHT_TOP });
      }
    } else {
      this.handleWindowResize();
    }
  };

  handleWindowResize = () => {
    let domNode;
    // fix  "Unable to find node on an unmounted component"
    try {
      domNode = ReactDOM.findDOMNode(this);
    } catch (e) {
      return;
    }

    const domRect = domNode.getBoundingClientRect();
    const tooltipOffset = domRect.left;
    const windowWidth = window.innerWidth;
    const diff = windowWidth - tooltipOffset;
    if (diff < tooltipWidth) {
      if (
        this.state.position === POSITIONS.LEFT_TOP ||
        this.state.position === POSITIONS.RIGHT_TOP
      ) {
        this.setState({ position: POSITIONS.LEFT_TOP });
      } else {
        this.setState({ position: POSITIONS.LEFT });
      }
    } else if (diff >= tooltipWidth) {
      if (
        this.state.position === POSITIONS.RIGHT_TOP ||
        this.state.position === POSITIONS.LEFT_TOP
      ) {
        this.setState({ position: POSITIONS.RIGHT_TOP });
      } else {
        this.setState({ position: null });
      }
    }
  };

  onShow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.handleFit();
    window.addEventListener('resize', this.handleWindowResize);
    !this.props.isDummy && this.setState({ show: true });
  };

  onClose = () => {
    window.removeEventListener('resize', this.handleWindowResize);
    this.setState({ show: false });
  };

  handleWrapperClick = (e) => {
    const target = e.target;
    if (target.tagName.toLowerCase() === 'a') {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  render() {
    let {
      title,
      content,
      hasWrapper,
      position,
      isFormClass,
      closeByClick,
      fixed,
      customIcon,
      hasCloseIcon,
      isDummy,
    } = this.props;
    const { show, position: statePosition } = this.state;
    const currentPosition = statePosition || position;
    const classes = classnames(
      'help',
      'js-help-parent',
      { help__form: isFormClass },
      { dummy: isDummy },
    );
    const iconClass = classnames(styles.tooltipIcon, 'icon', customIcon, 'js-help-btn');
    return (
      <div ref={this.$containerRef} className={classes}>
        <div className={iconClass} onClick={this.onShow}>
          <span className="replace_text">Помощь</span>
        </div>
        <CSSTransition in={show} timeout={200} classNames={styles.animation} unmountOnExit>
          <TooltipContainer
            title={title}
            content={content}
            hasWrapper={hasWrapper}
            currentPosition={currentPosition}
            onClose={this.onClose}
            handleWrapperClick={this.handleWrapperClick}
            closeByClick={closeByClick}
            fixed={fixed}
            show={true}
            hasCloseIcon={hasCloseIcon}
            outsideClickIgnoreClass={fixed ? 'relative-portal-container' : ''}
          />
        </CSSTransition>
      </div>
    );
  }
}
